import ExpandIcon from "@redotech/redo-icons/expand-01_filled.svg";
import {
  SortDirection,
  type TableSort,
} from "@redotech/redo-views-common/table";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { Flex } from "@redotech/redo-web/flex";
import {
  ColumnAlignment,
  type CsvColumn,
  type TableFetcher,
} from "@redotech/redo-web/table";
import type { Column2, DisplayedColumn } from "@redotech/redo-web/table2";
import { Table2 } from "@redotech/redo-web/table2";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import classNames from "classnames";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import type { TableItem } from "../hooks/use-intelligent-inventory";
import { PAGE_SIZE, selectTableRows } from "./table-card-rows";
import * as styles from "./table-card.module.css";

type Row = string[];

const DEFAULT_SORT: TableSort = { direction: SortDirection.ASC, key: "col-0" };

export const TableCard = memo(function TableCard({
  item,
  onExpand,
  expandedView = false,
}: {
  item: TableItem;
  /** Shows an expand affordance in the title row when provided. */
  onExpand?: () => void;
  /**
   * Rendered inside an expanded overlay: fill the available height and
   * omit the card title (the overlay header owns it).
   */
  expandedView?: boolean;
}) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [sort, setSort] = useState<TableSort>(DEFAULT_SORT);

  const handleSetSort = useCallback((nextSort: TableSort) => {
    setSort(nextSort);
    setPageNumber(0);
  }, []);

  const columns: Column2<Row>[] = useMemo(() => {
    const MAX_COL_WIDTH = 350;
    const MIN_COL_WIDTH = 80;
    const CHAR_WIDTH = 8;
    const CELL_PADDING = 32;

    return item.headers.map((header, colIndex) => {
      let maxLen = header.length;
      for (const row of item.rows) {
        const cellLen = (row[colIndex] ?? "").length;
        if (cellLen > maxLen) {
          maxLen = cellLen;
        }
      }
      const width = Math.min(
        MAX_COL_WIDTH,
        Math.max(MIN_COL_WIDTH, maxLen * CHAR_WIDTH + CELL_PADDING),
      );

      return {
        key: `col-${colIndex}`,
        title: header,
        alignment: ColumnAlignment.LEFT,
        sort: SortDirection.ASC,
        width,
        renderNormalCell: ({ row }: { row: Row }) => (
          <RedoTableTextCell text={row[colIndex] ?? ""} />
        ),
      };
    });
  }, [item.headers, item.rows]);

  const getCsvColumns = useCallback(
    (displayedColumns: DisplayedColumn[]): CsvColumn<Row>[] => {
      return displayedColumns
        .filter((col) => col.displayed)
        .map((col) => {
          const colIndex = item.headers.findIndex(
            (_, i) => `col-${i}` === col.key,
          );
          return {
            header: item.headers[colIndex] ?? col.key,
            cell: (row: Row) => row[colIndex] ?? "",
          };
        });
    },
    [item.headers],
  );

  const fetcher: TableFetcher<Row, true> = useMemo(
    () => ({
      sync: true as const,
      rows: item.rows,
      counts() {
        return { all: item.rows.length, total: item.rows.length };
      },
      data(_primaryFilter, _filters, _search, sort, pageSize, pageNumber) {
        return {
          items: selectTableRows(item.rows, item.headers, {
            sort,
            pageSize,
            pageNumber,
          }),
        };
      },
    }),
    [item.rows, item.headers],
  );

  const titleContent = useMemo(
    () => (
      <Flex align="center" gap="xs" grow={1}>
        <Flex align="center" grow={1}>
          {item.title && !expandedView ? (
            <Text fontSize="sm" fontWeight="semibold">
              {item.title}
            </Text>
          ) : null}
        </Flex>
        {onExpand ? (
          <Tooltip title="Expand table">
            <Flex align="center" as="span" gap="none">
              <RedoButton
                aria-label="Expand table"
                hierarchy="tertiary"
                IconLeading={ExpandIcon}
                onClick={onExpand}
                size="xs"
              />
            </Flex>
          </Tooltip>
        ) : null}
      </Flex>
    ),
    [item.title, expandedView, onExpand],
  );

  return (
    <div
      className={classNames(
        styles.card,
        expandedView && styles.expandedCard,
        item.rows.length > PAGE_SIZE && styles.paginated,
      )}
      ref={scrollAreaRef}
    >
      <Table2
        aboveTableContent={titleContent}
        adjustableColumnWidths
        columns={columns}
        columnSelection
        externalSetSort={handleSetSort}
        externalSort={sort}
        fetcher={fetcher}
        filename={item.title ?? "table"}
        getCsvColumns={getCsvColumns}
        pageNumber={pageNumber}
        pageSize={PAGE_SIZE}
        scrollAreaRef={scrollAreaRef}
        setPageNumber={setPageNumber}
        size="sm"
        sortDefault={DEFAULT_SORT}
      />
    </div>
  );
});
